import { EnergySavingsLeafOutlined } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';
import ReBarChart from 'switch-fe-shared/components/Charts/ReBarChart';
import type { PpaSiteMonth } from 'switch-shared/modules/dashboard/ppa-site.dto';
import { formatNumber } from 'switch-shared/utils/formatters';

import {
  formatPercent,
  getSolarChartColors,
  monthKeyLabel,
  performanceRatio,
} from '../../utils/solarPresentation';
import SolarCardState, { type SolarCardStatus } from './SolarCardState';

interface Props {
  status: SolarCardStatus;

  months: PpaSiteMonth[];

  /** Whether the site has a municipal meter point, without which its load is unknown. */
  hasMunicipal: boolean;
}

/** Monthly solar used on site and exported, with the share of the site's load solar supplied under each month. */
const SolarShareCard = ({ status, months, hasMunicipal }: Props) => {
  const theme = useTheme();
  const colors = getSolarChartColors(theme);

  const summary = useMemo(() => {
    const measured = months.filter((m) => m.hasSolarData);
    const sum = (pick: (m: PpaSiteMonth) => number) =>
      measured.reduce((total, m) => total + pick(m), 0);
    const share = hasMunicipal
      ? performanceRatio(
          sum((m) => m.selfConsumedKwh),
          sum((m) => m.siteLoadKwh)
        )
      : null;
    return {
      share: share == null ? '—' : formatPercent(share),
      co2Tonnes: formatNumber(sum((m) => m.co2AvoidedKg) / 1000, 1),
      subLabels: months.map((m) => {
        const ratio =
          hasMunicipal && m.hasSolarData
            ? performanceRatio(m.selfConsumedKwh, m.siteLoadKwh)
            : null;
        return ratio == null ? '—' : formatPercent(ratio);
      }),
    };
  }, [months, hasMunicipal]);
  const formatKwh = (value: number) => formatNumber(value, 0);

  return (
    <WidgetCard
      title='Solar Share & Carbon'
      subheader={`${summary.share} of site load from solar | ${summary.co2Tonnes} t CO₂e avoided | % under each month is solar share`}
      avatar={<EnergySavingsLeafOutlined />}
      cardContentProps={{
        sx: { display: 'flex', flexDirection: 'column', pb: '16px !important' },
      }}
    >
      <SolarCardState status={status}>
        <Box flex={1} minHeight={0}>
          <ReBarChart
            categories={months.map((m) => monthKeyLabel(m.key))}
            series={[
              {
                name: 'Used on site',
                data: months.map((m) => m.selfConsumedKwh),
                group: 'solar',
              },
              {
                name: 'Exported',
                data: months.map((m) => m.exportedKwh),
                group: 'solar',
              },
            ]}
            stackGroups
            showTooltipTotal
            colors={[colors.actual, colors.exported]}
            barOpacity={1}
            showLegend
            xSubLabels={summary.subLabels}
            yFormatter={formatKwh}
            unit='kWh'
            fileName='Solar Share & Carbon'
          />
        </Box>
      </SolarCardState>
    </WidgetCard>
  );
};

export default SolarShareCard;
